import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "@/components/Toast";
import { useAuth } from "@/contexts/AuthContext";
import { saludoMendoza, relojMendoza } from "@/lib/i18n";
import { Building2, MapPinned, ShieldAlert, FileText, Inbox, Activity, ChevronRight, CalendarX, Clock, Footprints } from "lucide-react";
import ViaticoAlertBanner from "@/components/ViaticoAlertBanner";
import { InstallPwaButton } from "@/components/InstallPwaButton";

const AR_TZ = "America/Argentina/Buenos_Aires";
const todayAR = () => new Date().toLocaleDateString("en-CA", { timeZone: AR_TZ });

/** Dashboard Supervisor (desktop). En mobile sigue funcionando porque el grid se adapta. */
export default function SupervisorDashboard() {
  const { user } = useAuth();
  const [m, setM] = useState(null);

  useEffect(() => {
    (async () => {
      try { const r = await api.get("/dashboard/metrics"); setM(r.data); }
      catch (e) { toast.error(formatApiError(e)); }
    })();
  }, []);

  const firstName = (user?.name || "").split(" ")[0] || "Supervisor";

  return (
    <div style={{ padding: "20px 28px 60px", maxWidth: 1400, margin: "0 auto" }}>
      <div style={{ background: "linear-gradient(135deg, #0A0A0A 0%, #1a0e0f 100%)", color: "#FFFBF4", padding: "30px 30px 26px", borderRadius: 6, marginBottom: 22, position: "relative", overflow: "hidden" }}>
        <div style={{ fontSize: 11, letterSpacing: "0.22em", color: "#BF3437", fontWeight: 800, textTransform: "uppercase", marginBottom: 6 }}>Supervisor · Mendoza · {relojMendoza()}</div>
        <h1 style={{ fontSize: 30, fontWeight: 900, margin: 0, letterSpacing: "-0.02em" }}>{saludoMendoza()}, <span style={{ color: "#BF3437" }}>{firstName}</span>.</h1>
        <p style={{ fontSize: 13, color: "rgba(255,251,244,0.7)", margin: "10px 0 0" }}>Centro operativo · Asignás rondas, gestionás novedades y respondés solicitudes.</p>
        <Link to="/supervisor/visitas" data-testid="action-ingreso" style={{
          display: "inline-flex", alignItems: "center", gap: 8, marginTop: 16, position: "relative", zIndex: 1,
          background: "#BF3437", color: "#FFFBF4", padding: "10px 18px", borderRadius: 4,
          fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em",
          textDecoration: "none",
        }}>
          <Footprints size={14} /> Cargar ingreso
        </Link>
        <ViaticoAlertBanner/>
        <div style={{ position: "absolute", right: -60, bottom: -40, width: 200, height: 200, background: "radial-gradient(circle, rgba(191,52,55,0.2), transparent 70%)" }} />
      </div>

      <InstallPwaButton />

      {!m ? <div className="skel" style={{ height: 120 }}/> : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 14, marginBottom: 22 }}>
            <Metric label="Servicios abiertos" value={m.open_services} icon={Activity} color="#16a34a" to="/supervisor/servicio" testId="m-svc"/>
            <Metric label="Personas adentro" value={m.people_inside} icon={Footprints} color="#0891b2" to="/supervisor/visitas" testId="m-people-inside"/>
            <Metric label="Rondas en curso" value={m.in_progress_patrols} icon={MapPinned} color="#0A0A0A" testId="m-pat"/>
            <Metric label="Rondas no realizadas" value={m.overdue_patrols} icon={CalendarX} color="#BF3437" highlight={m.overdue_patrols > 0} to="/supervisor/rondas?status=vencida_mes" testId="m-overdue-pat" sub={m.overdue_patrols_month_label ? `en ${m.overdue_patrols_month_label}` : undefined}/>
            <Metric label="Rondas pendientes" value={m.pending_patrols_today} icon={Clock} color="#0A0A0A" to={`/supervisor/rondas?status=pendiente&date=${todayAR()}`} testId="m-pending-pat-today" sub="hoy"/>
            <Metric label="Pánicos pendientes" value={m.panic_active} icon={ShieldAlert} color="#BF3437" highlight={m.panic_active > 0} testId="m-panic"/>
            <Metric label="Novedades hoy" value={m.today_news} icon={FileText} color="#0A0A0A" testId="m-news"/>
            <Metric label="Solicitudes pend." value={m.pending_requests} icon={Inbox} color="#f59e0b" testId="m-req"/>
            <Metric label="Clientes activos" value={m.active_clients} icon={Building2} color="#0A0A0A" testId="m-cli"/>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 12 }}>
            <QuickLink to="/supervisor/rondas" label="Asignar rondas" desc="Crear y editar rondas de vigiladores" icon={MapPinned}/>
            <QuickLink to="/supervisor/novedades" label="Cargar novedad" desc="Registrar incidentes con fotos/video" icon={FileText}/>
            <QuickLink to="/supervisor/servicio" label="Servicios abiertos" desc="Abrir, cerrar y ver el estado por objetivo" icon={Activity}/>
            <QuickLink to="/supervisor/servicios" label="Mis Servicios" desc="Objetivos, sedes y chat por vigilador" icon={Activity}/>
            {/* Fix AY (2026-09-30): sin el tipo de sueldo, que se sacó en AA. */}
            <QuickLink to="/supervisor/solicitudes" label="Solicitudes" desc="Refuerzos, compras, materiales" icon={Inbox}/>
          </div>
        </>
      )}
    </div>
  );
}

function Metric({ label, value, icon: Ic, color, highlight, testId, to, sub }) {
  const content = (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div style={{ fontSize: 10, letterSpacing: "0.16em", color: "var(--invict-text-muted)", fontWeight: 700, textTransform: "uppercase" }}>{label}</div>
          <div style={{ fontSize: 30, fontWeight: 900, color, marginTop: 6, lineHeight: 1 }}>{value}</div>
          {sub && <div style={{ fontSize: 10, color: "var(--invict-text-muted)", marginTop: 4 }} data-testid={`${testId}-sub`}>{sub}</div>}
        </div>
        <Ic size={20} style={{ color, opacity: 0.5 }} />
      </div>
    </>
  );
  const style = { padding: 16, borderLeft: `4px solid ${color}`, background: highlight ? "rgba(191,52,55,0.04)" : undefined };
  if (to) {
    return <Link to={to} className="card" data-testid={testId} style={{ ...style, textDecoration: "none", color: "inherit", display: "block" }}>{content}</Link>;
  }
  return <div className="card" data-testid={testId} style={style}>{content}</div>;
}

function QuickLink({ to, label, desc, icon: Ic }) {
  return (
    <Link to={to} className="card" style={{ padding: 18, textDecoration: "none", color: "inherit", display: "flex", alignItems: "center", gap: 14 }} data-testid={`ql-${to.split("/").pop()}`}>
      <Ic size={26} style={{ color: "#BF3437" }} />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 900, color: "#0A0A0A" }}>{label}</div>
        <div style={{ fontSize: 11, color: "var(--invict-text-muted)", marginTop: 2 }}>{desc}</div>
      </div>
      <ChevronRight size={16} style={{ color: "var(--invict-text-muted)" }} />
    </Link>
  );
}
